import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Breadcrumb } from "@/components/breadcrumb";
import { ClientAccessDeniedCard } from "@/components/clients/client-access-denied-card";
import { clientAccessDenialOf } from "@/lib/api/client-access-denial";
import { useSidebar } from "@/lib/context/sidebar-context";
import { useStandaloneInstanceSource } from "@/lib/context/standalone-instance-context";
import { useClient } from "@/lib/hooks/use-client-detail";
import { useTenantTheme } from "@/lib/hooks/use-clients";
import {
  useFieldValues,
  useFlowDefinition,
  useFlowInstanceGoals,
  useFlowInstanceMetadata,
} from "@/lib/hooks/use-flow-instances";
import { useFlowCompletion } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { useT } from "@/lib/i18n/use-t";
import { FlowInstanceWizard } from "./flow-instance-wizard";
import { T } from "./flow-instance-wizard-wrapper.i18n";
import { FieldProgressBar } from "./flow-progress-bar";

type FlowInstanceWizardWrapperProps = {
  instanceId: string;
  clientId: string;
};

export function FlowInstanceWizardWrapper({
  instanceId,
  clientId,
}: FlowInstanceWizardWrapperProps) {
  // ── Split queries (all fire in parallel) ──
  const {
    metadata,
    error: metaError,
    isLoading: metaLoading,
    mutate: refreshMetadata,
  } = useFlowInstanceMetadata(instanceId);
  const { client, error: clientError, isLoading: clientLoading } = useClient(clientId);
  const { flowDefinition, isLoading: flowLoading } = useFlowDefinition(instanceId);
  const { fieldValues, isLoading: valuesLoading } = useFieldValues(instanceId);
  const { goals, isLoading: goalsLoading } = useFlowInstanceGoals(instanceId);
  const { theme } = useTenantTheme();
  const t = useT(T);
  const { completion } = useFlowCompletion(instanceId);
  const { setSupportContext } = useSidebar();
  const { standalone } = useStandaloneMode();
  // QL-261 / QL-262 — publish the persisted internalRedirectUrl to the root
  // standalone store so useStandaloneMode() (the wizard's post-submit redirect,
  // the navigation guard) prefers it over the link param.
  useStandaloneInstanceSource(metadata?.internalRedirectUrl);

  const isLoading = metaLoading || clientLoading || flowLoading || valuesLoading || goalsLoading;

  const flowName = flowDefinition?.name;
  const clientName = client?.name;

  useEffect(() => {
    if (!flowName || !clientName) return;
    setSupportContext({
      entityType: "flow_instance",
      entityId: instanceId,
      displayName: `${flowName} — ${clientName}`,
    });
    return () => setSupportContext(null);
  }, [instanceId, flowName, clientName, setSupportContext]);

  // QL-380 — a restricted client answers 403 on every read; show why, not a spinner.
  const denial = clientAccessDenialOf(metaError) ?? clientAccessDenialOf(clientError);
  if (denial) return <ClientAccessDeniedCard denial={denial} />;

  if (isLoading || !metadata || !client || !flowDefinition) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Assemble instance shape from split queries ──
  const flowDef = flowDefinition as any;
  const instance = {
    ...metadata,
    client,
    flow: flowDef,
    values: fieldValues?.values || [],
    goals: goals || [],
  } as any;

  // Extract product types and portfolio costs from the portfolio collection
  const portfolioCollection = flowDef?.portfolioCollection;
  const productTypes = portfolioCollection?.productTypes || [];
  const portfolioCosts = portfolioCollection?.portfolioCosts || [];

  return (
    <div className="space-y-6">
      <div className={`flex items-start gap-4 ${standalone ? "justify-end" : "justify-between"}`}>
        {/* QL-33 standalone mode: no breadcrumbs out of the flow */}
        {!standalone && (
          <Breadcrumb
            items={[
              { label: t.clients, href: "/clients" },
              { label: instance.client?.name || "Client", href: `/clients/${clientId}` },
              { label: metadata.name || instance.flow.name },
            ]}
          />
        )}
        {metadata.status !== "completed" && completion && (
          <FieldProgressBar
            filledCount={completion.filledRequired}
            totalCount={completion.totalRequired}
            percent={completion.percent}
          />
        )}
      </div>

      <FlowInstanceWizard
        instance={instance}
        clientId={instance.clientId}
        productTypes={productTypes}
        portfolioCosts={portfolioCosts}
        chartColors={theme?.chartColors}
        refreshInstance={refreshMetadata}
      />
    </div>
  );
}
